/**
 * @fileoverview Edit Prospect mutation hook.
 * @description Saves a prospect's five sections with its ETag as If-Match (PATCH
 * /sales/prospects/{prospectId}) and refreshes every sales query once it succeeds. The ETag is the
 * one the dialog loaded and showed, so a prospect that changed since is refused (`412` / `409`) and
 * shown for review rather than overwritten (3.5.11.0).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { editProspect } from '@/features/sales/services/prospects.service'

import type { EditProspectRequest, ProspectEdited } from '@/features/sales/types/prospect.types'

/** The input of one edit command. */
export interface EditProspectInput {
  prospectId: string
  etag: string
  request: EditProspectRequest
}

/**
 * @description Edits a prospect. The command's idempotency key outlives the dialog, so a retry after
 * an unknown outcome replays it.
 * @param {object} [options] - What follows a success.
 * @param {(result: ProspectEdited, input: EditProspectInput) => void} [options.onSuccess] - Called
 * after the edit is saved, even when the dialog has unmounted since.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the prospect, the
 * ETag the dialog loaded and showed, and the request).
 */
export function useEditProspect({
  onSuccess,
}: {
  onSuccess?: ((result: ProspectEdited, input: EditProspectInput) => void) | undefined
} = {}) {
  return useIdempotentMutation<EditProspectInput, ProspectEdited>({
    action: 'edit-prospect',
    /**
     * @description Scopes the key to the prospect and the edit made, not the version it is sent against.
     * @param {EditProspectInput} input - The edit's input.
     * @returns {object} The prospect id and the edit.
     */
    scopeOf: ({ prospectId, request }) => ({ prospectId, request }),
    /**
     * @description Sends the edit under the key it is given.
     * @param {EditProspectInput} input - The prospect, its version and the edit.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<ProspectEdited>} The edited prospect.
     */
    send: ({ prospectId, etag, request }, key) => editProspect(prospectId, etag, request, key),
    onSuccess,
  })
}
